import * as Sentry from '@sentry/react';

type DomOperation = 'removeChild' | 'insertBefore';

type DomSignalSnapshot = {
  url: string;
  userAgent: string;
  language: string;
  htmlLang: string;
  htmlTranslate: string | null;
  bodyClasses: string;
  hasGoogleTranslateFrame: boolean;
  hasGoogleTranslateBanner: boolean;
  hasTranslatedClass: boolean;
  activeElement: string | null;
  rootChildCount: number | null;
};

type GuardTelemetry = {
  op: DomOperation;
  target: string;
  child?: string;
  reference?: string;
  actualParent?: string | null;
  signals: DomSignalSnapshot;
};

declare global {
  interface Window {
    __eposDomGuardsInstalled__?: boolean;
    __eposDomGuardHits__?: Record<string, number>;
    __eposLastDomGuardTelemetry__?: GuardTelemetry;
  }
}

const GUARD_EVENT_LIMIT = 5;

const selectorFromNode = (node: Node | null | undefined): string | undefined => {
  if (!node) return undefined;

  if (node instanceof HTMLElement) {
    const tag = node.tagName.toLowerCase();
    const id = node.id ? `#${node.id}` : '';
    const className =
      typeof node.className === 'string'
        ? node.className
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 3)
            .map((item) => `.${item}`)
            .join('')
        : '';
    return `${tag}${id}${className}`;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    const preview = node.textContent?.trim().slice(0, 30) ?? '';
    return `#text(${preview})`;
  }

  return node.nodeName;
};

const getDomSignalSnapshot = (): DomSignalSnapshot => {
  const html = document.documentElement;
  const body = document.body;
  const root = document.getElementById('root');

  return {
    url: window.location.toString(),
    userAgent: navigator.userAgent,
    language: navigator.language,
    htmlLang: html.lang,
    htmlTranslate: html.getAttribute('translate'),
    bodyClasses: body?.className ?? '',
    hasGoogleTranslateFrame: !!document.querySelector(
      'iframe.goog-te-banner-frame, iframe.goog-te-menu-frame'
    ),
    hasGoogleTranslateBanner: !!document.querySelector(
      '.goog-te-banner-frame, .goog-te-gadget, .goog-te-combo'
    ),
    hasTranslatedClass:
      html.classList.contains('translated-ltr') ||
      html.classList.contains('translated-rtl') ||
      body?.classList.contains('translated-ltr') ||
      body?.classList.contains('translated-rtl') ||
      false,
    activeElement: selectorFromNode(document.activeElement) ?? null,
    rootChildCount: root?.childNodes.length ?? null,
  };
};

const recordGuardTelemetry = (telemetry: GuardTelemetry) => {
  window.__eposLastDomGuardTelemetry__ = telemetry;

  const key = `${telemetry.op}:${telemetry.target}:${telemetry.reference ?? ''}`;
  const currentHits = (window.__eposDomGuardHits__?.[key] ?? 0) + 1;
  window.__eposDomGuardHits__ = {
    ...(window.__eposDomGuardHits__ ?? {}),
    [key]: currentHits,
  };

  Sentry.addBreadcrumb({
    category: 'dom.guard',
    level: 'warning',
    message: `Guarded DOM ${telemetry.op} mismatch`,
    data: telemetry,
  });

  if (currentHits > GUARD_EVENT_LIMIT) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('dom_guard', 'true');
    scope.setTag('dom_operation', telemetry.op);
    scope.setTag(
      'dom_external_mutation_suspected',
      String(
        telemetry.signals.hasGoogleTranslateBanner ||
          telemetry.signals.hasGoogleTranslateFrame ||
          telemetry.signals.hasTranslatedClass
      )
    );
    scope.setContext('dom_guard', telemetry);
    Sentry.captureMessage(`Guarded DOM ${telemetry.op} mismatch`);
  });
};

const getTelemetry = (
  op: DomOperation,
  target: Node,
  child?: Node | null,
  reference?: Node | null
): GuardTelemetry => ({
  op,
  target: selectorFromNode(target) ?? 'unknown',
  child: selectorFromNode(child),
  reference: selectorFromNode(reference),
  actualParent: selectorFromNode((child ?? reference)?.parentNode),
  signals: getDomSignalSnapshot(),
});

const isNode = (value: unknown): value is Node => value instanceof Node;

export const getLastDomGuardTelemetry = () => window.__eposLastDomGuardTelemetry__;

export const installDomMutationGuards = () => {
  if (typeof window === 'undefined' || window.__eposDomGuardsInstalled__) {
    return;
  }

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;
  const originalAppendChild = Node.prototype.appendChild;

  Node.prototype.removeChild = function patchedRemoveChild<T extends Node>(child: T): T {
    if (!isNode(child)) {
      return originalRemoveChild.call(this, child) as T;
    }

    if (child.parentNode === this) {
      return originalRemoveChild.call(this, child) as T;
    }

    recordGuardTelemetry(getTelemetry('removeChild', this, child));

    if (child.parentNode) {
      return originalRemoveChild.call(child.parentNode, child) as T;
    }

    return child;
  };

  Node.prototype.insertBefore = function patchedInsertBefore<T extends Node>(
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (!referenceNode || referenceNode.parentNode === this) {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    }

    recordGuardTelemetry(getTelemetry('insertBefore', this, newNode, referenceNode));

    if (newNode.parentNode && newNode.parentNode !== this) {
      originalRemoveChild.call(newNode.parentNode, newNode);
    }

    return originalAppendChild.call(this, newNode) as T;
  };

  window.__eposDomGuardsInstalled__ = true;
};
